import { AbstractUserObjectDisplayPlugin } from 'app/components/map/ol/plugin/AbstractUserObjectDisplayPlugin';
import MapUtils, { MapClickElement } from 'app/components/map/util/MapUtils';
import { FlightLevelBand, TimePeriod } from 'app/model/CORE';
import OLMap from 'app/components/map/ol/OLMap';
import Feature from 'ol/Feature';
import { StyleLike } from 'ol/style/Style';
import OLUtils from 'app/components/map/ol/OLUtils';
import * as ol from 'ol';
import MapConstants from 'app/components/map/MapConstants';
import * as geom from 'ol/geom';
import * as style from 'ol/style';
import OLMapStyles from 'app/components/map/ol/OLMapStyles';
import {
    MapLayer,
    MapLayerType,
    MapObjectType,
    WeatherLayerUtils,
    WeatherMinLevel,
} from 'app/components/dashboard/network/map/Map';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import { ConvectionFraction, ConvectionSource, Likeliness, MeteoForecast, PolygonMeteoForecast } from 'app/model/SOL49';
import { MapConvection } from 'app/components/map/CoreMap';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import Utils from 'app/common/util/Utils';
import TimeUtils from 'app/common/util/TimeUtils';
import AppContext from 'app/common/context/AppContext';

export const VALIDITY_PERIOD_PROPERTY_NAME: string = 'validityPeriod';
export const TV_PROPERTY_NAME: string = 'tv';

export class PolygonConvectionMapDisplayPlugin extends AbstractUserObjectDisplayPlugin {
    private readonly PLUGIN_LAYER_TYPE = MapLayerType.WEATHER;
    private readonly PLUGIN_MAP_OBJECT_TYPE = MapObjectType.CONVECTION;

    canBeDisplayed(layer: MapLayer, object: any, timePeriod: TimePeriod, flightLevelBand: FlightLevelBand): boolean {
        let refTime: Date | TimePeriod = timePeriod;
        if (timePeriod.from === timePeriod.to) {
            refTime = timePeriod.from;
        }
        return (
            this.isMapElement(layer, this.PLUGIN_MAP_OBJECT_TYPE) &&
            this.PLUGIN_LAYER_TYPE === layer.type &&
            object['convection'] &&
            object['convection']['polygon'] && //DWD
            object['tvAirspaceMap'] &&
            object['convection']['validityPeriod'] &&
            TimePeriodUtils.intersects(object['convection']['validityPeriod'] as TimePeriod, refTime) &&
            FlightLevelUtils.intersects(flightLevelBand, (object['convection'] as MeteoForecast)?.flightLevelBand) && //TODO rely on airspace instead?
            (layer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_MIN_LEVEL] as WeatherMinLevel)?.getLevel() <=
                (object['convection']['level'] as number) &&
            (!layer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_HIDE_CONVECTIONS] ||
                (Utils.isExists(object['tvAirspaceMap']) && Utils.isNotEmpty(Object.keys(object['tvAirspaceMap']))))
        );
    }

    display(
        olMap: OLMap,
        object: any,
        mapLayer: MapLayer,
        timePeriod: TimePeriod,
        flightLevelBand: FlightLevelBand,
    ): Feature | Feature[] {
        const mac = object as MapConvection;
        if (!MapUtils.shouldDisplayPolygonConvection(mac.convection.validityPeriod, timePeriod)) return [];

        const convection: PolygonMeteoForecast = mac.convection;
        const showTvs = mapLayer.customProperties[WeatherLayerUtils.CUSTOM_PROPERTY_SHOW_TVS];

        if (showTvs) {
            const showTvFeatures: Feature[] = [];
            Object.entries(mac.tvAirspaceMap).forEach(([tv, airspace], idx) => {
                let polygons: geom.Polygon[];
                const airspaceOutline = olMap.getAirspaceOutline(airspace);
                if (airspaceOutline) {
                    polygons = OLUtils.buildPolygons(airspaceOutline);
                } else {
                    polygons = OLUtils.buildPolygons(airspace);
                }
                let features: ol.Feature[];
                let feature: ol.Feature;
                if (mapLayer.showLabel) {
                    features = olMap.getPolygonArrayFeatureWithLabel(polygons, `${airspace.reference.id}`);
                    feature = features[1];
                } else {
                    feature = new ol.Feature(new geom.MultiPolygon(polygons));
                    features = [feature];
                }
                feature.set(MapConstants.ATTR_TIME_PERIOD, mac.convection.validityPeriod);
                feature.set(MapConstants.ATTR_LABEL, convection.id);
                feature.set(MapConstants.ATTR_TYPE, MapConstants.POLYGON_CONVECTION_TYPE);
                feature.set(MapConstants.ATTR_TV_NAME, tv);
                feature.set(MapConstants.ATTR_MAJOR_IDX, idx);
                features[0].set(MapConstants.ATTR_MAP_LAYER_ID, mapLayer.id);
                if (AppContext.getConvectionSource() === ConvectionSource.DWD) {
                    let fillColor: number[];
                    let strokeColor: number[];
                    switch (convection.likeliness) {
                        case Likeliness.LESS_LIKELY:
                            switch (convection.convectionFraction) {
                                case ConvectionFraction.ISOL:
                                    break;
                                case ConvectionFraction.CLST:
                                    strokeColor = [255, 255, 0, 1];
                                    fillColor = [255, 255, 0, 0.25];
                                    break;
                                case ConvectionFraction.WSPR:
                                    strokeColor = [252, 140, 4, 1];
                                    fillColor = [252, 140, 4, 0.25];
                                    break;
                            }
                            break;
                        case Likeliness.LIKELY:
                            switch (convection.convectionFraction) {
                                case ConvectionFraction.ISOL:
                                    strokeColor = [255, 255, 127, 1];
                                    fillColor = [255, 255, 127, 0.25];
                                    break;
                                case ConvectionFraction.CLST:
                                    strokeColor = [255, 192, 0, 1];
                                    fillColor = [255, 192, 0, 0.25];
                                    break;
                                case ConvectionFraction.WSPR:
                                    strokeColor = [255, 0, 0, 1];
                                    fillColor = [255, 0, 0, 0.25];
                                    break;
                            }
                            break;
                        case Likeliness.VERY_LIKELY:
                            switch (convection.convectionFraction) {
                                case ConvectionFraction.ISOL:
                                    strokeColor = [255, 217, 102, 1];
                                    fillColor = [255, 217, 102, 0.25];
                                    break;
                                case ConvectionFraction.CLST:
                                    strokeColor = [255, 102, 102, 1];
                                    fillColor = [255, 102, 102, 0.25];
                                    break;
                                case ConvectionFraction.WSPR:
                                    strokeColor = [112, 48, 160, 1];
                                    fillColor = [112, 48, 160, 0.25];
                                    break;
                            }
                            break;
                    }
                    feature.setStyle(
                        new style.Style({
                            stroke: new style.Stroke({
                                color: strokeColor,
                                width: 1,
                            }),
                            fill: new style.Fill({
                                color: fillColor,
                            }),
                        }),
                    );
                } else {
                    feature.setStyle(this.getMapAirspaceConvectionStyle(mac));
                }
                showTvFeatures.push(...features);
            });
            const points: number[][] = convection.polygon.map(p => [p.longitude, p.latitude]);
            const lineString = new geom.LineString(points);
            const outlineFeature = new ol.Feature(lineString);
            outlineFeature.setStyle(
                new style.Style({
                    stroke: new style.Stroke({
                        color: [180, 180, 180, 1],
                        width: 2,
                    }),
                }),
            );
            showTvFeatures.push(outlineFeature);
            return showTvFeatures;
        } else {
            const polygon = OLUtils.buildPolygonFromArray(convection.polygon);
            const convectionFeature = new ol.Feature(polygon);
            convectionFeature.set(MapConstants.ATTR_LABEL, convection.id);
            convectionFeature.set(MapConstants.ATTR_TIME_PERIOD, convection.validityPeriod);
            convectionFeature.set(MapConstants.ATTR_TYPE, MapConstants.POLYGON_CONVECTION_TYPE);
            convectionFeature.set(MapConstants.ATTR_MAP_LAYER_ID, mapLayer.id);
            let style;
            if (AppContext.getConvectionSource() === ConvectionSource.DWD) {
                style = MapUtils.getPolygonDWDConvectionStyle(convection.convectionFraction, convection.likeliness);
            } else if (AppContext.getConvectionSource() === ConvectionSource.DWD_ICONV) {
                style = MapUtils.getPolygonIconvConvectionStyle(
                    mac.convection.level,
                    convection.validityPeriod,
                    timePeriod,
                );
            }
            if (style && !TimePeriodUtils.intersects(mac.convection.validityPeriod, TimeUtils.NOW())) {
                style.getStroke().setLineDash(OLMapStyles.LINE_DASH);
            }
            convectionFeature.setStyle(style);
            return convectionFeature;
        }
    }

    getStyle(mapLayer: MapLayer): StyleLike {
        return null;
    }

    processClickEvent(features: Array<Feature>, clientXY: number[]): Array<MapClickElement> {
        return null;
    }

    processRightClickEvent(features: Array<Feature>, clientXY: number[]): Array<MapClickElement> {
        const clickElements: Array<MapClickElement> = [];
        for (const feature of features) {
            const type: string = feature.get(MapConstants.ATTR_TYPE);

            //sanity check
            if (type === MapConstants.POLYGON_CONVECTION_TYPE) {
                const convectionId: string = feature.get(MapConstants.ATTR_LABEL);
                const validityPeriod: TimePeriod = feature.get(MapConstants.ATTR_TIME_PERIOD);
                const tv: string = feature.get(MapConstants.ATTR_TV_NAME);
                clickElements.push({
                    type: MapObjectType.CONVECTION,
                    subject: convectionId,
                    layerId: feature.get(MapConstants.ATTR_MAP_LAYER_ID),
                    properties: {
                        [VALIDITY_PERIOD_PROPERTY_NAME]: validityPeriod,
                        [TV_PROPERTY_NAME]: Utils.isExists(tv) ? tv : undefined,
                    },
                });
            }
        }
        return clickElements;
    }

    private getMapAirspaceConvectionStyle(input: MapConvection): style.Style {
        switch (input.convection.level) {
            case 0: {
                return OLMapStyles.CONVECTION_LEVEL_ZERO_STYLE.clone();
            }
            case 1: {
                return OLMapStyles.CONVECTION_LEVEL_ONE_STYLE.clone();
            }
            case 2: {
                return OLMapStyles.CONVECTION_LEVEL_TWO_STYLE.clone();
            }
            case 3: {
                return OLMapStyles.CONVECTION_LEVEL_THREE_STYLE.clone();
            }
            case 4: {
                return OLMapStyles.CONVECTION_LEVEL_FOUR_STYLE.clone();
            }
        }
        return null;
    }
}
